// oxlint-disable react/react-compiler -- React Compiler bails out of this file; remove when fixed
import { styled } from "goober";
import type { QueryRowType } from "@rocicorp/zero";
import { useCallback, useMemo, useRef, useState } from "react";
import { useZero } from "@rocicorp/zero/react";
import { queries, roundedDaysAgo } from "../../../shared/queries";
import { sharedMutators } from "../../../shared/mutators";
import type { MonitoringWorkflowValueResponse } from "../../../shared/workflowValueReport";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useQuery } from "../../hooks/useQuery";
import { useRevisionedJson } from "../../hooks/useRevisionedJson";
import { monitoringWorkflowValuePath } from "../../lib/apiPaths";
import { useUrlSort } from "../../hooks/useUrlSort";
import {
  type DisplayableSpace,
  useSpaceDisplayName,
} from "../../hooks/useSpaceDisplayName";
import { makeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { useMonitoringAnalytics, useMonitoringInteraction } from "./analytics";

import { spaceIcon } from "../../lib/spaceIcon";
import {
  ConnectorDropdown,
  MIN_OPTIONS_FOR_SEARCH,
  RowButton,
  SectionLabel,
} from "../ConnectorDropdown";
import { Dropdown } from "../Dropdown";
import { Icon } from "../Icon";
import { Input } from "../Input";
import { ListFilter, type FilterDef } from "../ListFilter";
import { ListTable } from "../ListTable";
import { Skeleton } from "../Skeleton";
import { Stepper, StepButton } from "../Stepper";
import { META_SEP } from "../MetaSeparator";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { HelpTooltip } from "../HelpTooltip";
import {
  StatusBreakdownChart,
  type StatusSegment,
} from "../StatusBreakdownChart";
import { WorkflowBadge } from "../WorkflowBadge";
import type { AiUsageActivity } from "./aiUsageActivities";
import { AI_USAGE_ACTIVITY_LABELS } from "./aiUsageActivities";
import { StatCell } from "./AiOverviewStats";
import { ControlButton } from "./ControlButton";
import { MonitoringLayout } from "./MonitoringLayout";
import { MonitoringScopeSelector } from "./MonitoringScopeSelector";
import {
  foldsPersonal,
  PERSONAL_BUCKET,
  PERSONAL_BUCKET_COLOR,
} from "./monitoringScope";
import { useMonitoringScope } from "./useMonitoringScope";
import { NameBody, NameCell, ScrollArea, TableCard } from "./monitoringList";
import {
  computeAiValueSummary,
  computeWorkflowAiValueRows,
  formatSignedValueUsd,
  formatValueUsd,
  productivityRateFromBudgets,
  type WorkflowAiValueRow,
  type WorkflowValueWorkflow,
} from "./workflowAiValue";
import { SPACE_COLORS } from "./monitoringPanelLayout";
import { splitBySharePrefix } from "./foldSegments";
import { OTHER_SPACES_KEY, SPACE_FOLD } from "./spaceSegments";

type MonitoringWorkflow = QueryRowType<
  ReturnType<typeof queries.monitoringWorkflowValue>
>;

const DEFAULT_WINDOW = { value: "30", label: "30 days", days: 30 } as const;

// Each option is a distinct aggregate the endpoint recomputes from scratch, so
// the window is a short fixed list rather than a free-form range.
const WINDOW_OPTIONS = [
  DEFAULT_WINDOW,
  { value: "60", label: "60 days", days: 60 },
  { value: "90", label: "90 days", days: 90 },
] as const;

function windowOption(param: string) {
  return (
    WINDOW_OPTIONS.find((option) => option.value === param) ?? DEFAULT_WINDOW
  );
}

const RATE_STEP = 5;
const RATE_INPUT_STYLE = {
  minWidth: 0,
  width: 44,
  height: 24,
  textAlign: "center",
  padding: "0 4px",
  fontSize: 10,
  lineHeight: "12px",
  flexShrink: 0,
} as const;

const RateStepButton = styled(StepButton)`
  width: 24px;
  height: 24px;
  border-radius: 9999px;
`;

const Content = styled("div")`
  flex: 1;
  min-height: 0;
  padding: 0 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SummaryCard = styled("div")`
  flex: 1;
  min-height: 0;
  padding: 32px;
  border-radius: 12px;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const SummaryHeader = styled("div")`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  flex-shrink: 0;
  container-type: inline-size;
`;

const StatRow = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: 40px;
  align-items: flex-start;
`;

const StatsRow = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: clamp(24px, 5cqi, 64px);
  align-items: stretch;
`;

const ValueBox = styled("div")`
  flex: 1;
  display: flex;
  align-items: center;
`;

const StatLabelRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const VALUE_STAT_TOOLTIPS = {
  total:
    "The total savings from these workflows over the selected time range, based on your hourly rate. It combines one-time build savings with ongoing run savings.",
  build:
    "The savings from building these workflows with 3B rather than manually coding.",
  run: "The savings from powering these workflows in 3B rather than manually performing the steps.",
  workflows:
    "The number of workflows contributing to savings over the given time range.",
} as const;

function StatLabel({ label, tooltip }: { label: string; tooltip: string }) {
  return (
    <StatLabelRow>
      <Text variant="text-small" color="var(--text-secondary)">
        {label}
      </Text>
      <HelpTooltip content={tooltip} />
    </StatLabelRow>
  );
}

const MONITORING_GRID =
  "minmax(200px, 3fr) minmax(80px, 1fr) minmax(80px, 1fr) minmax(80px, 1fr) minmax(60px, 0.75fr)";

const SubValue = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
`;

const SumRow = styled("div")`
  display: grid;
  grid-template-columns: ${MONITORING_GRID};
  align-items: center;
  border-top: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
  flex-shrink: 0;
`;

const SumCell = styled("div")`
  display: flex;
  align-items: center;
  padding: 14px 8px;

  &:first-child {
    padding-left: 24px;
  }
  &:last-child {
    padding-right: 24px;
  }
`;

function toWorkflowValueWorkflow(
  workflow: MonitoringWorkflow
): WorkflowValueWorkflow {
  return {
    id: workflow.id,
    name: workflow.name,
    spaceId: workflow.spaceId,
    estimatedMinutesSaved: workflow.estimatedMinutesSaved,
    estimatedBuildMinutesSaved: workflow.estimatedBuildMinutesSaved,
    timeSavedSource: workflow.timeSavedSource,
    timeSavedBuildSource: workflow.timeSavedBuildSource,
  };
}

type WorkflowValueRow = {
  workflowId: string;
  name: string;
  spaceId: string;
  badgeSeed: string | null;
  pausedAt: number | null;
  space: DisplayableSpace | null;
  branches: readonly { name: string }[];
  totalValueUsd: number;
  buildValueUsd: number;
  runValueUsd: number;
  buildMinutes: number | null;
  runMinutes: number | null;
  buildSource: "llm" | "manual" | null;
  runSource: "llm" | "manual" | null;
};

type SpaceValueRow = {
  spaceId: string;
  space: DisplayableSpace | null;
  label: string;
  totalValueUsd: number;
  buildValueUsd: number;
  runValueUsd: number;
  workflowCount: number;
};

function computeWorkflowValueRows(
  valueRows: readonly WorkflowAiValueRow[],
  workflows: readonly MonitoringWorkflow[]
): WorkflowValueRow[] {
  const workflowById = new Map(workflows.map((w) => [w.id, w]));

  const rows: WorkflowValueRow[] = [];
  for (const row of valueRows) {
    const wf = workflowById.get(row.workflowId);
    if (!wf) continue;
    rows.push({
      workflowId: row.workflowId,
      name: wf.name,
      spaceId: wf.spaceId,
      badgeSeed: wf.badgeSeed,
      pausedAt: wf.pausedAt,
      space: wf.space ?? null,
      branches: wf.branches ?? [],
      totalValueUsd: row.totalGrossValueUsd ?? 0,
      buildValueUsd: row.byActivity.build.grossValueUsd ?? 0,
      runValueUsd: row.byActivity.run.grossValueUsd ?? 0,
      buildMinutes: row.byActivity.build.minutesPerEvent,
      runMinutes: row.byActivity.run.minutesPerEvent,
      buildSource: row.byActivity.build.source,
      runSource: row.byActivity.run.source,
    });
  }

  return rows.sort(
    (a, b) => b.totalValueUsd - a.totalValueUsd || a.name.localeCompare(b.name)
  );
}

function computeSpaceValueRows(
  workflowRows: readonly WorkflowValueRow[],
  spaces: readonly DisplayableSpace[],
  displayNameFn: (s: DisplayableSpace) => string
): SpaceValueRow[] {
  const bySpace = new Map<
    string,
    { build: number; run: number; count: number }
  >();
  for (const row of workflowRows) {
    const entry = bySpace.get(row.spaceId) ?? {
      build: 0,
      run: 0,
      count: 0,
    };
    entry.build += row.buildValueUsd;
    entry.run += row.runValueUsd;
    entry.count += 1;
    bySpace.set(row.spaceId, entry);
  }

  const spaceById = new Map(spaces.map((s) => [s.id, s]));
  const rows: SpaceValueRow[] = [];
  for (const [spaceId, vals] of bySpace) {
    const space = spaceById.get(spaceId);
    if (!space) continue;
    rows.push({
      spaceId,
      space,
      label: displayNameFn(space),
      totalValueUsd: vals.build + vals.run,
      buildValueUsd: vals.build,
      runValueUsd: vals.run,
      workflowCount: vals.count,
    });
  }

  return rows.sort(
    (a, b) =>
      b.totalValueUsd - a.totalValueUsd || a.label.localeCompare(b.label)
  );
}

function sourceLabel(source: "llm" | "manual" | null): string {
  if (source === "manual") return "set by user";
  if (source === "llm") return "AI estimate";
  return "not set";
}

function LoadingPlaceholder() {
  return (
    <>
      <Skeleton
        aria-hidden
        style={{ width: "100%", height: 72, borderRadius: 12, flexShrink: 0 }}
      />
      <Skeleton
        aria-hidden
        style={{ width: "100%", flex: 1, minHeight: 200, borderRadius: 12 }}
      />
    </>
  );
}

export function Value() {
  const monitoringAnalytics = useMonitoringAnalytics();
  const recordInteraction = useMonitoringInteraction();
  const { window: windowParam = "30", space: spaceParam } = useCurrentUrl(
    routes.monitoring.value
  );
  const selectedWindow = windowOption(windowParam);
  const selectedCheck = (
    <Icon name="check" size={12} color="var(--text-secondary)" />
  );
  const rowCheck = (
    <span style={{ flexShrink: 0, display: "flex", marginLeft: "auto" }}>
      {selectedCheck}
    </span>
  );
  // The window counts today, so a 30 day window starts 29 day boundaries back.
  // The endpoint derives the same boundary from `days`.
  const since = roundedDaysAgo(selectedWindow.days - 1);
  const selectedSpaceId = spaceParam ?? null;
  const setSelectedSpaceId = useCallback(
    (id: string | null) => {
      recordInteraction("value.space_filter_change", {
        targetType: "space",
        targetId: id ?? undefined,
      });
      navigate(routes.monitoring.value, { space: id ?? "" }, { replace: true });
    },
    [recordInteraction]
  );
  const zero = useZero();
  const canManageAI = useHasAccess("tenant:ai_providers_manage");
  // Read already collapsed rather than synced row by row: the ledger's grain is
  // far finer than the single bit of it this tab draws.
  const { data: valueUsage, loading: usageLoading } =
    useRevisionedJson<MonitoringWorkflowValueResponse>(
      monitoringWorkflowValuePath(),
      `days=${selectedWindow.days}`
    );
  // ai_budgets is read only for the productivity hourly rate, which has no
  // connector-ledger equivalent.
  const [budgets, budgetsQuery] = useQuery(queries.monitoringAiBudgets());
  const [workflows, workflowsQuery] = useQuery(
    queries.monitoringWorkflowValue()
  );
  const [dailyStats, dailyStatsQuery] = useQuery(
    queries.monitorWorkflowDailyStats({ since })
  );
  const [spaces] = useQuery(queries.spacesLite({ includeAdminSpaces: true }));
  const { scope, personalIds, matchesSpace } = useMonitoringScope(
    routes.monitoring.value
  );
  const displayName = useSpaceDisplayName();

  // Hold the last completed windowed result so changing the time window swaps
  // data in place instead of blanking while the new window's query reloads. A
  // failed fetch resolves with no payload, and clearing on that would read as a
  // tenant that has built nothing at all.
  const heldBuildWorkflowIds = useRef<readonly string[]>([]);
  const buildWorkflowIdList =
    !usageLoading && valueUsage
      ? valueUsage.buildWorkflowIds
      : heldBuildWorkflowIds.current;
  heldBuildWorkflowIds.current = buildWorkflowIdList;

  const lastDailyStats = useRef(dailyStats);
  if (dailyStatsQuery.type === "complete") {
    lastDailyStats.current = dailyStats;
  }
  const dailyStatsView =
    dailyStatsQuery.type === "complete" ? dailyStats : lastDailyStats.current;

  const buildWorkflowIds = useMemo(
    () => new Set(buildWorkflowIdList),
    [buildWorkflowIdList]
  );
  // Value by space, the space filter, and the space dropdown all follow the
  // header scope, so scope the summary and table to the same workflows too —
  // otherwise the header total would count value the space chart omits.
  const scopedWorkflows = useMemo(
    () => (workflows ?? []).filter((w) => matchesSpace(w.spaceId)),
    [workflows, matchesSpace]
  );
  const workflowInputs = useMemo(
    () => scopedWorkflows.map(toWorkflowValueWorkflow),
    [scopedWorkflows]
  );
  // Run value is per successful run, so exclude failed runs from the count.
  const executionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const workflow of dailyStatsView ?? []) {
      for (const row of workflow.runDailyStats) {
        const successful = (row.runCount ?? 0) - (row.errorRunCount ?? 0);
        if (successful <= 0) continue;
        counts.set(workflow.id, (counts.get(workflow.id) ?? 0) + successful);
      }
    }
    return counts;
  }, [dailyStatsView]);
  const hourlyRateUsd = useMemo(
    () => productivityRateFromBudgets(budgets ?? []),
    [budgets]
  );
  const [rateInput, setRateInput] = useState<string | null>(null);

  const valueRows = useMemo(
    () =>
      computeWorkflowAiValueRows(
        buildWorkflowIds,
        workflowInputs,
        executionCounts,
        hourlyRateUsd
      ),
    [buildWorkflowIds, workflowInputs, executionCounts, hourlyRateUsd]
  );

  const inSelectedSpace = useCallback(
    (rowSpaceId: string) =>
      selectedSpaceId == null
        ? true
        : selectedSpaceId === PERSONAL_BUCKET
          ? personalIds.has(rowSpaceId)
          : rowSpaceId === selectedSpaceId,
    [selectedSpaceId, personalIds]
  );

  const visibleValueRows = useMemo(
    () => valueRows.filter((r) => inSelectedSpace(r.spaceId)),
    [valueRows, inSelectedSpace]
  );

  const valueSummary = useMemo(
    () => computeAiValueSummary(visibleValueRows, "all"),
    [visibleValueRows]
  );

  const workflowRows = useMemo(
    () => computeWorkflowValueRows(valueRows, scopedWorkflows),
    [valueRows, scopedWorkflows]
  );

  const scopedSpaces = useMemo(
    () => (spaces ?? []).filter((s) => matchesSpace(s.id)),
    [spaces, matchesSpace]
  );

  const spaceRows = useMemo(() => {
    const rows = computeSpaceValueRows(workflowRows, scopedSpaces, displayName);
    if (!foldsPersonal(scope)) return rows;
    const team: SpaceValueRow[] = [];
    let build = 0;
    let run = 0;
    let count = 0;
    for (const row of rows) {
      if (personalIds.has(row.spaceId)) {
        build += row.buildValueUsd;
        run += row.runValueUsd;
        count += row.workflowCount;
      } else {
        team.push(row);
      }
    }
    if (count === 0) return team;
    team.push({
      spaceId: PERSONAL_BUCKET,
      space: null,
      label: "Personal",
      totalValueUsd: build + run,
      buildValueUsd: build,
      runValueUsd: run,
      workflowCount: count,
    });
    return team.sort(
      (a, b) =>
        b.totalValueUsd - a.totalValueUsd || a.label.localeCompare(b.label)
    );
  }, [workflowRows, scopedSpaces, scope, personalIds, displayName]);

  const filteredWorkflowRows = useMemo(
    () => workflowRows.filter((r) => inSelectedSpace(r.spaceId)),
    [workflowRows, inSelectedSpace]
  );

  const selectedSpaceIndex = selectedSpaceId
    ? spaceRows.findIndex((r) => r.spaceId === selectedSpaceId)
    : -1;
  const selectedSpace =
    selectedSpaceIndex >= 0 ? spaceRows[selectedSpaceIndex] : null;

  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.monitoring.value,
    { name: "asc", build: "desc", run: "desc", total: "desc" }
  );
  const sortAndRecord = (column: string) => {
    recordInteraction("value.table_sort", { value: column });
    handleSort(column);
  };

  const sortedWorkflowRows = useMemo(() => {
    const rows = [...filteredWorkflowRows];
    if (!sortColumn) return rows;
    const dir = sortDirection === "asc" ? 1 : -1;
    const metric = (r: WorkflowValueRow) =>
      sortColumn === "build"
        ? r.buildValueUsd
        : sortColumn === "run"
          ? r.runValueUsd
          : r.totalValueUsd;
    rows.sort((a, b) =>
      sortColumn === "name"
        ? a.name.localeCompare(b.name) * dir
        : (metric(a) - metric(b)) * dir || a.name.localeCompare(b.name)
    );
    return rows;
  }, [filteredWorkflowRows, sortColumn, sortDirection]);

  const spaceFilters: FilterDef[] = useMemo(
    () => [
      {
        kind: "single",
        key: "space",
        label: "Space",
        icon: <Icon name="hash" size={16} />,
        options: spaceRows.map((r) => ({
          id: r.spaceId,
          label: r.label,
          menuLeft: (
            <Icon name={spaceIcon(r.space?.mode ?? "personal")} size={16} />
          ),
          pillLeft: (
            <Icon name={spaceIcon(r.space?.mode ?? "personal")} size={12} />
          ),
        })),
        value: selectedSpaceId,
        onSelect: (id) => setSelectedSpaceId(id),
        onClear: () => setSelectedSpaceId(null),
      },
    ],
    [spaceRows, selectedSpaceId, setSelectedSpaceId]
  );

  const spaceValueTotal = useMemo(
    () => spaceRows.reduce((sum, r) => sum + r.totalValueUsd, 0),
    [spaceRows]
  );
  const spaceSegments = useMemo<StatusSegment[]>(() => {
    // Fold the small-savings tail into one "Other"; kept spaces keep their rich
    // build/run breakdown, and Other lists the folded spaces.
    const { kept, folded } = splitBySharePrefix(
      spaceRows,
      (row) => row.totalValueUsd,
      spaceValueTotal,
      SPACE_FOLD
    );
    const segments: StatusSegment[] = kept.map((row, i) => ({
      key: row.spaceId,
      count: row.totalValueUsd,
      color:
        row.spaceId === PERSONAL_BUCKET
          ? PERSONAL_BUCKET_COLOR
          : SPACE_COLORS[i % SPACE_COLORS.length]!,
      label: row.label,
      details: [
        { label: "Total", value: formatValueUsd(row.totalValueUsd) },
        { label: "Build", value: formatValueUsd(row.buildValueUsd || null) },
        { label: "Run", value: formatValueUsd(row.runValueUsd || null) },
        { label: "Workflows", value: String(row.workflowCount) },
      ],
    }));
    if (folded.length > 0) {
      segments.push({
        key: OTHER_SPACES_KEY,
        count: folded.reduce((sum, row) => sum + row.totalValueUsd, 0),
        color: "var(--gray-600)",
        label: `${folded.length} more spaces`,
        details: folded.map((row) => ({
          label: row.label,
          value: formatValueUsd(row.totalValueUsd),
        })),
      });
    }
    return segments;
  }, [spaceRows, spaceValueTotal]);

  const buildValue = useMemo(
    () => computeAiValueSummary(visibleValueRows, "build").grossValueUsd,
    [visibleValueRows]
  );
  const runValue = useMemo(
    () => computeAiValueSummary(visibleValueRows, "run").grossValueUsd,
    [visibleValueRows]
  );

  const isLoading =
    usageLoading ||
    budgetsQuery.type !== "complete" ||
    workflowsQuery.type !== "complete" ||
    dailyStatsQuery.type !== "complete";

  // Only block the whole page on the first load; window changes refetch the
  // windowed queries but should keep the rendered content (with held data).
  const hasLoadedOnce = useRef(false);
  if (!isLoading) hasLoadedOnce.current = true;

  const tenantBudget = (budgets ?? []).find(
    (b) => b.scopeKind === "tenant" && !b.aiProviderId
  );

  async function commitRate(value: string) {
    const parsed = parseFloat(value);
    if (Number.isNaN(parsed) || parsed <= 0) {
      setRateInput(null);
      return;
    }
    setRateInput(null);
    if (parsed === hourlyRateUsd) return;
    zero.mutate(
      sharedMutators.updateAIBudget({
        analytics: monitoringAnalytics(),
        id: tenantBudget?.id,
        scopeKind: "tenant",
        productivityHourlyRate: parsed,
      })
    );
  }

  function adjustRate(delta: number) {
    const parsed = parseFloat(rateInput ?? String(hourlyRateUsd));
    const base = Number.isFinite(parsed) ? parsed : hourlyRateUsd;
    commitRate(String(Math.max(RATE_STEP, base + delta)));
  }

  function setWindow(value: string) {
    recordInteraction("value.window_change", { value });
    navigate(routes.monitoring.value, { window: value }, { replace: true });
  }

  return (
    <MonitoringLayout
      headerItem={<MonitoringScopeSelector route={routes.monitoring.value} />}
    >
      <Content>
        {isLoading && !hasLoadedOnce.current ? (
          <LoadingPlaceholder />
        ) : (
          <SummaryCard>
            <SummaryHeader>
              <StatsRow>
                <StatCell>
                  <StatLabel
                    label="Total"
                    tooltip={VALUE_STAT_TOOLTIPS.total}
                  />
                  <ValueBox>
                    <Text variant="display-md" color="var(--text-primary)">
                      {formatSignedValueUsd(valueSummary.grossValueUsd ?? 0)}
                    </Text>
                  </ValueBox>
                </StatCell>
                {(
                  [
                    { key: "build", value: buildValue },
                    { key: "run", value: runValue },
                  ] as const
                ).map(({ key, value }) => (
                  <StatCell key={key}>
                    <StatLabel
                      label={AI_USAGE_ACTIVITY_LABELS[key as AiUsageActivity]}
                      tooltip={VALUE_STAT_TOOLTIPS[key]}
                    />
                    <ValueBox>
                      <Text variant="display-md">
                        {formatSignedValueUsd(value ?? 0)}
                      </Text>
                    </ValueBox>
                  </StatCell>
                ))}
                <StatCell>
                  <StatLabel
                    label="Workflows"
                    tooltip={VALUE_STAT_TOOLTIPS.workflows}
                  />
                  <ValueBox>
                    <Text variant="display-md">
                      {valueSummary.workflowCount}
                    </Text>
                  </ValueBox>
                </StatCell>
              </StatsRow>
              <StatRow>
                <StatCell>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Space
                  </Text>
                  <ConnectorDropdown
                    searchable={spaceRows.length >= MIN_OPTIONS_FOR_SEARCH}
                    searchPlaceholder="Search spaces…"
                    opener={
                      <ControlButton type="button">
                        {selectedSpace ? (
                          <Icon
                            name={spaceIcon(
                              selectedSpace.space?.mode ?? "personal"
                            )}
                            size={12}
                          />
                        ) : (
                          <Icon name="layers" size={12} />
                        )}
                        <Text variant="text-small-md">
                          {selectedSpace ? selectedSpace.label : "All spaces"}
                        </Text>
                        <Icon
                          name="chevron-down"
                          size={12}
                          color="var(--text-secondary)"
                        />
                      </ControlButton>
                    }
                  >
                    {(query, close) => {
                      const q = query.trim().toLowerCase();
                      const matches = spaceRows.filter(
                        (r) => !q || r.label.toLowerCase().includes(q)
                      );
                      return (
                        <>
                          {!q && (
                            <RowButton
                              data-nav-row=""
                              onClick={() => {
                                setSelectedSpaceId(null);
                                close();
                              }}
                            >
                              <Icon
                                name="layers"
                                size={16}
                                color="var(--text-primary)"
                              />
                              <div
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  textAlign: "left",
                                }}
                              >
                                <Text variant="text-regular" ellipsis>
                                  All spaces
                                </Text>
                                <Text
                                  variant="text-small"
                                  color="var(--text-secondary)"
                                  ellipsis
                                >
                                  {spaceRows.length}{" "}
                                  {spaceRows.length === 1 ? "space" : "spaces"}
                                </Text>
                              </div>
                              {selectedSpaceId == null && rowCheck}
                            </RowButton>
                          )}
                          {matches.length === 0 && q !== "" ? (
                            <SectionLabel>
                              <Text
                                variant="text-small"
                                color="var(--text-secondary)"
                              >
                                No spaces found.
                              </Text>
                            </SectionLabel>
                          ) : (
                            matches.map((r) => (
                              <RowButton
                                key={r.spaceId}
                                data-nav-row=""
                                onClick={() => {
                                  setSelectedSpaceId(r.spaceId);
                                  close();
                                }}
                              >
                                <Icon
                                  name={spaceIcon(r.space?.mode ?? "personal")}
                                  size={16}
                                />
                                <div
                                  style={{
                                    flex: 1,
                                    minWidth: 0,
                                    textAlign: "left",
                                  }}
                                >
                                  <Text variant="text-regular" ellipsis>
                                    {r.label}
                                  </Text>
                                </div>
                                {selectedSpaceId === r.spaceId && rowCheck}
                              </RowButton>
                            ))
                          )}
                        </>
                      );
                    }}
                  </ConnectorDropdown>
                </StatCell>
                <StatCell>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Time range
                  </Text>
                  <Dropdown
                    align="bottom"
                    opener={
                      <ControlButton type="button">
                        <Text variant="text-small-md">
                          {selectedWindow.label}
                        </Text>
                        <Icon
                          name="chevron-down"
                          size={12}
                          color="var(--text-secondary)"
                        />
                      </ControlButton>
                    }
                    sections={[
                      {
                        items: WINDOW_OPTIONS.map((opt) => ({
                          type: "button" as const,
                          key: opt.value,
                          left: null,
                          label: opt.label,
                          selected: windowParam === opt.value,
                          right:
                            windowParam === opt.value
                              ? selectedCheck
                              : undefined,
                          onClick: () => setWindow(opt.value),
                        })),
                      },
                    ]}
                  />
                </StatCell>
                <StatCell>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Hourly rate ($)
                  </Text>
                  <Stepper>
                    <RateStepButton
                      aria-label="Decrease hourly rate"
                      disabled={
                        !canManageAI ||
                        (parseFloat(rateInput ?? String(hourlyRateUsd)) ||
                          hourlyRateUsd) <= RATE_STEP
                      }
                      onPress={() => adjustRate(-RATE_STEP)}
                    >
                      <Icon
                        name="minus"
                        size={12}
                        color="var(--text-secondary)"
                      />
                    </RateStepButton>
                    <Input
                      value={rateInput ?? String(hourlyRateUsd)}
                      inputMode="numeric"
                      disabled={!canManageAI}
                      onChange={(e) => setRateInput(e.target.value)}
                      onBlur={(e) => commitRate(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          commitRate(e.currentTarget.value);
                        }
                        if (e.key === "Escape") setRateInput(null);
                      }}
                      style={
                        canManageAI
                          ? RATE_INPUT_STYLE
                          : { ...RATE_INPUT_STYLE, opacity: 0.4 }
                      }
                    />
                    <RateStepButton
                      aria-label="Increase hourly rate"
                      disabled={!canManageAI}
                      onPress={() => adjustRate(RATE_STEP)}
                    >
                      <Icon
                        name="plus"
                        size={12}
                        color="var(--text-secondary)"
                      />
                    </RateStepButton>
                  </Stepper>
                </StatCell>
              </StatRow>
            </SummaryHeader>
            <StatusBreakdownChart
              title="Savings by space"
              total={spaceValueTotal}
              segments={spaceSegments}
              formatValue={formatValueUsd}
              embedded
              wrapLegend
              showLegendValue={false}
              placeholder={spaceRows.length === 0}
              activeKey={selectedSpaceId}
              onSegmentClick={(s) => {
                if (s.key === OTHER_SPACES_KEY) return;
                setSelectedSpaceId(selectedSpaceId === s.key ? null : s.key);
              }}
            />
            <TableCard>
              <ScrollArea>
                <ListTable
                  headers={{
                    name: "Workflow",
                    build: "Build",
                    run: "Run",
                    total: "Total",
                    actions: "",
                  }}
                  columns={{
                    name: "flex",
                    build: "md",
                    run: "md",
                    total: "md",
                    actions: "sm",
                  }}
                  sortableColumns={sortableColumns}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={sortAndRecord}
                  headerActions={
                    workflowRows.length > 0 ? (
                      <ListFilter filters={spaceFilters} />
                    ) : undefined
                  }
                  emptyState={
                    <Text variant="text-regular" color="var(--text-secondary)">
                      {selectedSpaceId
                        ? "No workflows with savings in this space."
                        : "No workflows with savings in this window."}
                    </Text>
                  }
                  items={sortedWorkflowRows.map((row) => ({
                    key: row.workflowId,
                    link: makeUrl(routes.modal.workflowValue, {
                      id: row.workflowId,
                    }),
                    onNavigate: () =>
                      recordInteraction("value.workflow_value_modal_open", {
                        targetType: "workflow",
                        targetId: row.workflowId,
                      }),
                    name: (hover) => (
                      <NameCell>
                        <WorkflowBadge
                          workflow={{
                            id: row.workflowId,
                            badgeSeed: row.badgeSeed,
                            pausedAt: row.pausedAt,
                            branches: row.branches,
                          }}
                          hover={hover}
                        />
                        <NameBody>
                          <Text variant="text-regular-md" ellipsis>
                            {row.name}
                          </Text>
                          {row.space && (
                            <Text
                              variant="text-small"
                              color="var(--text-secondary)"
                              ellipsis
                            >
                              {displayName(row.space)}
                            </Text>
                          )}
                        </NameBody>
                      </NameCell>
                    ),
                    build: (
                      <Tooltip
                        content={`${row.buildMinutes ?? "–"} min to build${META_SEP}${sourceLabel(row.buildSource)}`}
                      >
                        <SubValue>
                          <Text variant="text-regular-md">
                            {formatValueUsd(row.buildValueUsd || null)}
                          </Text>
                          <Text
                            variant="text-small"
                            color="var(--text-secondary)"
                          >
                            {row.buildMinutes != null
                              ? `${row.buildMinutes}m`
                              : "–"}
                          </Text>
                        </SubValue>
                      </Tooltip>
                    ),
                    run: (
                      <Tooltip
                        content={`${row.runMinutes ?? "–"} min saved/run${META_SEP}${sourceLabel(row.runSource)}`}
                      >
                        <SubValue>
                          <Text variant="text-regular-md">
                            {formatValueUsd(row.runValueUsd || null)}
                          </Text>
                          <Text
                            variant="text-small"
                            color="var(--text-secondary)"
                          >
                            {row.runMinutes != null
                              ? `${row.runMinutes}m/run`
                              : "–"}
                          </Text>
                        </SubValue>
                      </Tooltip>
                    ),
                    total: (
                      <Text variant="text-regular-md" color="var(--green-600)">
                        {formatValueUsd(row.totalValueUsd)}
                      </Text>
                    ),
                    actions: null,
                  }))}
                />
              </ScrollArea>
              {filteredWorkflowRows.length > 0 && (
                <SumRow>
                  <SumCell>
                    <Text variant="text-regular-md">Total</Text>
                  </SumCell>
                  <SumCell>
                    <Text variant="text-regular-md">
                      {formatValueUsd(
                        filteredWorkflowRows.reduce(
                          (s, r) => s + r.buildValueUsd,
                          0
                        ) || null
                      )}
                    </Text>
                  </SumCell>
                  <SumCell>
                    <Text variant="text-regular-md">
                      {formatValueUsd(
                        filteredWorkflowRows.reduce(
                          (s, r) => s + r.runValueUsd,
                          0
                        ) || null
                      )}
                    </Text>
                  </SumCell>
                  <SumCell>
                    <Text variant="text-regular-md" color="var(--green-600)">
                      {formatValueUsd(
                        filteredWorkflowRows.reduce(
                          (s, r) => s + r.totalValueUsd,
                          0
                        )
                      )}
                    </Text>
                  </SumCell>
                  <SumCell />
                </SumRow>
              )}
            </TableCard>
          </SummaryCard>
        )}
      </Content>
    </MonitoringLayout>
  );
}
